import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';

import { EditorContent } from '@tiptap/react';
import { EditorContext } from 'container/context/editorContext';

import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';

import ToolBar from './Toolbar';
import PopupMenu from './PopupMenu';
import CustomFloatingMenu from './CustomFloatingMenu';
import Subscription from '../Subscription/Subscription';
import PaymentForm from 'container/pages/Home/components/Content/PaymentForm';

const TipTapEditor = ({ title }) => {
  const [selected, setSelected] = useState('Body');
  const [selectionRange, setSelectionRange] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(null);
  const isDark = useSelector(state => state.utils.isDarkMode);

  const { selected: selectedDocument } = useSelector(state => state.documents);
  const subscriptionModal = useSelector(state => state.utils.subscriptionModal);
  const selectedPlan = useSelector(state => state.subscription.planId);
  const scroller = useRef(null);
  const { shouldUpdate } = useSelector(state => state.documents);
  const dispatch = useDispatch();

  const editor = useContext(EditorContext);

  const handleTrackSelection = () => {
    const selection = window.getSelection();

    if (selection.toString().length > 0) {
      setSelectionRange(selection.getRangeAt(0));
    } else {
      setSelectionRange(selectionRange);
    }
    setCursorPosition(selection.getRangeAt(0));
  };

  const handleClick = name => {
    switch (name) {
      case 'bold':
        editor.chain().focus().toggleBold().run();
        break;
      case 'italic':
        editor.chain().focus().toggleItalic().run();
        break;
      case 'underline':
        editor.commands.toggleUnderline();
        editor.chain().focus();
        break;
      case 'strike':
        editor.chain().focus().toggleStrike().run();
        break;
      case 'superscript':
        editor.chain().focus().toggleSuperscript().run();
        break;
      case 'subscript':
        editor.chain().focus().toggleSubscript().run();
        break;
      case 'align-left':
        editor.commands.setTextAlign('left');
        editor.chain().focus();
        break;
      case 'align-center':
        editor.commands.setTextAlign('center');
        editor.chain().focus();
        break;
      case 'order-list':
        editor.chain().focus().toggleOrderedList().run();
        break;
      case 'unorder-list':
        editor.chain().focus().toggleBulletList().run();
        break;
      case 'undo':
        editor.chain().undo().run();
        break;
      case 'redo':
        editor.chain().redo().run();
        break;
      case 'highlight':
        editor.chain().focus().toggleHighlight().run();
        break;
      case 'code':
        editor.chain().focus().toggleCode().run();
        break;
      case 'codeBlock':
        editor.chain().focus().toggleCodeBlock().run();
        break;
      default:
        break;
    }
  };

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      setSelected('Body');
    }
  };

  useEffect(() => {
    if (editor) {
      editor.chain().focus();
    }
    // eslint-disable-next-line
  }, [editor]);

  useEffect(() => {
    editor.on('selectionUpdate', () => {
      handleTrackSelection();
    });
    // eslint-disable-next-line
  }, [editor]);

  useEffect(() => {
    if (editor && selectedDocument && shouldUpdate) {
      editor.commands.setContent(selectedDocument.body);
      dispatch.utils.setIsDocText(false);
    } else {
      dispatch.documents.setShouldUpdate(true);
    }
    // eslint-disable-next-line
  }, [selectedDocument]);

  useEffect(() => {
    if (scroller.current) {
      scroller.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedPlan, subscriptionModal]);

  return (
    <Container isDark={isDark}>
      <ToolBar
        setLink={setLink}
        handleClick={handleClick}
        selected={selected}
        setSelected={setSelected}
        title={title}
      />
      <Editor isDark={isDark}>
        {
          // isDocText ? (<>
          //   <div className='flex text-center align-items-center justify-content-center h-100'>
          //     <div className='flex flex-column text-center'>
          //       <img className='text-center w-100' src={newAssets.logo} alt='logo' height={50} width={234.11} />
          //       <span className='hello-user'>Hello, {userData?.first_name || userData?.username}</span>
          //     </div>
          //   </div>
          // </>) :
          <>
            <EditorContent
              editor={editor}
              autoFocus={true}
              onKeyDown={handleKeyDown}
              // onMouseUp={handleTrackSelection}
            />
          </>
        }

        <SubscriptionContainer>
          {subscriptionModal && (
            <div>
              <Subscription containerStyle={{ padding: '0px' }} />
            </div>
          )}
          {selectedPlan && (
            <PaymentForm
              plan={selectedPlan}
              containerStyle={{ padding: '0px' }}
            />
          )}
        </SubscriptionContainer>
        <div className='scroll' ref={scroller}></div>
      </Editor>

      <PopupMenu
        handleClick={handleClick}
        setLink={setLink}
        selectionRange={selectionRange}
      />
      <CustomFloatingMenu selectionRange={cursorPosition} />
    </Container>
  );
};

export default TipTapEditor;

// onClick={() => {
//   let sel;
//   if (window.getSelection()) {
//     sel = window.getSelection();
//     if (sel.getRangeAt && sel.rangeCount) {
//       const range = sel.getRangeAt(0);
//       range.deleteContents();
//       range.insertNode(
//         document.createTextNode('This is a pasted text')
//       );
//     }
//   }
// }}

const Container = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: ${({ isDark }) =>
    isDark ? '#0e0f10' : 'rgba(255, 255, 255, 1)'};

  & .hello-user {
    font-family: Poppins;
    font-size: 32px;
    line-height: 48px;
    text-align: center;
    font-weight: 600;
    background: linear-gradient(
      95.23deg,
      #7e3fe4 -6.59%,
      #df3587 32.62%,
      #fe395c 71.84%,
      #fb9631 111.05%
    );
    /* background: linear-gradient(95.23deg, #7E3FE4 -6.59%, #DF3587 32.62%, #FE395C 71.84%, #FB9631 111.05%); */
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-fill-color: transparent;
  }
`;

const SubscriptionContainer = styled.div`
  padding-inline: 10px;
  padding-bottom: 10px;
`;

const Editor = styled.div`
  flex-grow: 1;
  height: 80dvh;
  overflow: auto;

  @media only screen and (min-width: 1400px) {
    height: 80dvh !important;
  }

  & .tiptap {
    outline: none;
    padding: 20px 70px;
    flex-grow: 1;
    color: ${({ isDark }) => (isDark ? '#FFFFFF' : 'var(--new-dark-color)')};
  }

  @media only screen and (min-width: 1400px) {
    height: 89dvh;
  }

  @media only screen and (max-width: 1000px) {
    /* height: calc(100dvh - 103px) !important; */
    height: 65dvh !important;
    & .tiptap {
      outline: none;
      padding: 20px;
      flex-grow: 1;
    }
  }
`;
